import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import AdminSearchBar from './AdminSearchBar';
import { useActingAthlete } from '../context/ActingAthleteContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import EmailVerificationBanner from './EmailVerificationBanner';
import { isAthlete as isAthleteRole } from '../utils/roles';
import './Layout.css';
import './Editorial.css';

export default function Layout({ children }) {
  const { user, logout, API_URL } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingFlags, setPendingFlags] = useState(0);
  const [unopenedReports, setUnopenedReports] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileUrl, setProfileUrl] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const [campsDropdownOpen, setCampsDropdownOpen] = useState(false);
  const [conciergeDropdownOpen, setConciergeDropdownOpen] = useState(false);
  const [messagesDropdownOpen, setMessagesDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const adminDropdownRef = useRef(null);
  const campsDropdownRef = useRef(null);
  const conciergeDropdownRef = useRef(null);
  const messagesDropdownRef = useRef(null);

  const MESSAGING_URL = `${API_URL.replace('/api', '')}/messaging`;

  // Acting-context flag — when a guardian taps an athlete chip, the
  // nav should render the athlete tabs (Profile, Recruiting, Messages,
  // Settings) instead of the empty guardian-only nav.
  const { actingAthleteId } = useActingAthlete();
  const isGuardian = user?.role === 'guardian';
  const isGuardianActing = isGuardian && actingAthleteId !== null;

  const isAdmin = user && (user.role === 'admin' || user.role === 'internal_staff');
  const isCoach = user?.role === 'coach';
  const isBasicCoach = user?.role === 'basic_coach';
  // isPlayer covers any athlete-side account: the canonical `athlete` role
  // plus the legacy `player_parent`/`base_user` values the backend still
  // emits during the compatibility window.
  const isPlayer = isAthleteRole(user?.role);
  // Treat acting guardians as athletes for nav-visibility purposes.
  // The underlying API calls auto-thread ?as_athlete_id= so the data
  // shown is the athlete's, while the JWT identity stays the guardian's.
  const isAthlete = isPlayer || isGuardianActing;
  const isPartner = user?.role === 'partner';
  const isMarketing = user?.role === 'marketing';
  // Staff AND partners navigate from the left rail (AdminShell menuFor), so both
  // get the search-only top bar. Every other role keeps its tab links. Marketing
  // was a rail role until its rail items were removed (2026-10-15); it keeps the
  // plain top bar now rather than an empty rail.
  const usesRail = isAdmin || isPartner;
  const canMessage = isCoach || isPlayer || isPartner || isAdmin || isGuardianActing;
  const canSearchPlayers = isCoach || isAdmin || isPartner;

  // Where the "Profile" entry in the avatar dropdown should point. Admins get a
  // profile page too (mirrors the coach profile for now); everyone else without a
  // distinct profile page falls through to null (Settings covers them).
  const profilePath = isAthlete
    ? '/profile'
    : (isCoach || isPartner || isBasicCoach)
      ? '/coach-profile'
      : isAdmin
        ? '/admin-profile'
        : null;
  const profileActivePaths = isAthlete
    ? ['/profile', '/academics', '/photos', '/videos', '/social', '/my-sports', '/stats', '/measurables']
    : isAdmin
      ? ['/admin-profile']
      : ['/coach-profile', '/coach-academics', '/coach-photos', '/coach-videos', '/coach-social'];

  // Total notification count for bell icon
  const notificationCount = isAdmin ? pendingFlags : unreadMessages;

  useEffect(() => {
    if (user && canMessage) {
      fetchUnreadMessages();
      const interval = setInterval(fetchUnreadMessages, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    if (user && isAdmin) {
      fetchPendingFlags();
      const interval = setInterval(fetchPendingFlags, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchUnopenedReports = useCallback(() => {
    axios.get(`${API_URL}/reports/unopened-count`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    }).then((res) => setUnopenedReports(res.data?.count || 0)).catch(() => {});
  }, [API_URL]);

  useEffect(() => {
    if (user && isCoach) {
      fetchUnopenedReports();
      const interval = setInterval(fetchUnopenedReports, 30000);
      return () => clearInterval(interval);
    }
  }, [user, isCoach, fetchUnopenedReports]);

  useEffect(() => {
    if (user && isPlayer) {
      axios.get(`${API_URL}/athletes/profile`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      }).then(res => {
        if (res.data?.profile_url) setProfileUrl(res.data.profile_url);
      }).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
    setAdminDropdownOpen(false);
    setCampsDropdownOpen(false);
    setConciergeDropdownOpen(false);
    setMessagesDropdownOpen(false);
  }, [location.pathname]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(e.target)) {
        setAdminDropdownOpen(false);
      }
      if (campsDropdownRef.current && !campsDropdownRef.current.contains(e.target)) {
        setCampsDropdownOpen(false);
      }
      if (conciergeDropdownRef.current && !conciergeDropdownRef.current.contains(e.target)) {
        setConciergeDropdownOpen(false);
      }
      if (messagesDropdownRef.current && !messagesDropdownRef.current.contains(e.target)) {
        setMessagesDropdownOpen(false);
      }
    };
    if (dropdownOpen || adminDropdownOpen || campsDropdownOpen || messagesDropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen, adminDropdownOpen, campsDropdownOpen, messagesDropdownOpen]);

  const fetchUnreadMessages = async () => {
    try {
      const response = await axios.get(
        `${MESSAGING_URL}/stats/unread`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      setUnreadMessages(response.data.unread_messages || 0);
    } catch (error) {
      console.error('Error fetching unread messages:', error);
    }
  };

  const fetchPendingFlags = async () => {
    try {
      const [flagsRes, coachRes] = await Promise.allSettled([
        axios.get(`${MESSAGING_URL}/flags/count`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }),
        axios.get(`${API_URL}/admin/coach-review/pending`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }),
      ]);
      const flagCount = flagsRes.status === 'fulfilled' ? (flagsRes.value.data.pending_flags || 0) : 0;
      const coachCount = coachRes.status === 'fulfilled' ? (coachRes.value.data.length || 0) : 0;
      setPendingFlags(flagCount + coachCount);
    } catch (error) {
      // Silently fail — badge just won't show
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleShare = async () => {
    if (!profileUrl) return;
    const url = `${window.location.origin}/p/${profileUrl}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'My ProZpects Profile', url });
      } catch (e) { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        alert('Profile link copied to clipboard!');
      } catch (e) {
        console.error('Copy failed:', e);
      }
    }
  };

  const isActive = (path) => location.pathname === path;
  const isActiveGroup = (paths) => paths.some(p => location.pathname === p || location.pathname.startsWith(p));

  const getInitials = () => {
    const first = user?.first_name?.[0] || '';
    const last = user?.last_name?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  // Role-specific nav tabs (matching iOS MainTabView)
  const renderNavLinks = () => {
    if (isMarketing) {
      return (
        <>
          <Link to="/marketing/dashboard" className={`nav-link ${isActive('/marketing/dashboard') ? 'nav-link--active' : ''}`}>Dashboard</Link>
          <Link to="/marketing/schools" className={`nav-link ${isActive('/marketing/schools') ? 'nav-link--active' : ''}`}>Schools</Link>
          <Link to="/marketing/players" className={`nav-link ${isActive('/marketing/players') ? 'nav-link--active' : ''}`}>Players</Link>
        </>
      );
    }

    // Athlete tabs: Dashboard, Messages, Verify, Recruiting, Profile
    if (isAthlete) {
      return (
        <>
          <Link to="/dashboard" className={`nav-link ${isActive('/dashboard') ? 'nav-link--active' : ''}`}>Dashboard</Link>
          <Link to="/messages" className={`nav-link ${isActive('/messages') ? 'nav-link--active' : ''}`}>
            Messages
            {unreadMessages > 0 && <span className="nav-badge">{unreadMessages}</span>}
          </Link>
          <Link to="/camps" className={`nav-link ${isActiveGroup(['/camps']) ? 'nav-link--active' : ''}`}>Verify</Link>
          {/* Following is a tab inside Recruit now, so it is no longer a
              top-level destination. The /following route still exists. */}
          <Link to="/recruit" className={`nav-link ${isActiveGroup(['/recruit', '/matches', '/offers']) ? 'nav-link--active' : ''}`}>Recruiting</Link>
          {/* Acting guardians keep a way back to their athlete picker. */}
          {isGuardianActing && (
            <Link to="/guardian/athletes" className={`nav-link ${isActive('/guardian/athletes') ? 'nav-link--active' : ''}`}>My Athletes</Link>
          )}
        </>
      );
    }

    // Guardian (not currently acting on an athlete): just the athlete-management
    // picker. Once they enter an athlete, isAthlete becomes true and the athlete
    // tabs above render instead.
    if (isGuardian) {
      return (
        <>
          <Link to="/dashboard" className={`nav-link ${isActive('/dashboard') ? 'nav-link--active' : ''}`}>Dashboard</Link>
          <Link to="/guardian/athletes" className={`nav-link ${isActive('/guardian/athletes') ? 'nav-link--active' : ''}`}>My Athletes</Link>
        </>
      );
    }

    // Coach tabs: Dashboard, Messages, Search, Lists, Profile.
    // Partner USED to share this block, but usesRail (isAdmin || isPartner)
    // routes every partner to the search top bar above before renderNavLinks
    // is ever called, so an isPartner disjunct here was unreachable. See
    // Layout.test.jsx's "partner gets the search top bar, not the coach tabs".
    if (isCoach || isBasicCoach) {
      return (
        <>
          <Link to="/dashboard" className={`nav-link ${isActive('/dashboard') ? 'nav-link--active' : ''}`}>Dashboard</Link>
          {!isBasicCoach && (
            <Link to="/messages" className={`nav-link ${isActive('/messages') ? 'nav-link--active' : ''}`}>
              Messages
              {unreadMessages > 0 && <span className="nav-badge">{unreadMessages}</span>}
            </Link>
          )}
          {!isBasicCoach && (
            <Link to="/players" className={`nav-link ${isActive('/players') ? 'nav-link--active' : ''}`}>Search</Link>
          )}
          {!isBasicCoach && (
            <Link to="/saved-athletes" className={`nav-link ${isActiveGroup(['/saved-athletes']) ? 'nav-link--active' : ''}`}>Lists</Link>
          )}
          {!isBasicCoach && (
            <Link to="/coach-reports" className={`nav-link ${isActiveGroup(['/coach-reports']) ? 'nav-link--active' : ''}`}>
              Reports
              {unopenedReports > 0 && <span className="nav-badge">{unopenedReports}</span>}
            </Link>
          )}
        </>
      );
    }

    // Admin / Internal Staff tabs: Dashboard, Concierge, Camps & Webinars, Search, Admin, Messages
    // UNREACHABLE as of the top-bar search change: the only call site is the
    // `!isAdmin` branch in the navbar below, so an admin never reaches this.
    // Kept rather than deleted because it is the whole admin top-nav and
    // restoring it is a one-line revert at that call site — delete this block
    // once the search-only bar has stuck.
    if (isAdmin) {
      return (
        <>
          <Link to="/dashboard" className={`nav-link ${isActive('/dashboard') ? 'nav-link--active' : ''}`}>Dashboard</Link>
          <div className="nav-dropdown-wrap" ref={conciergeDropdownRef}>
            <button
              className={`nav-link ${isActiveGroup(['/concierge', '/lists']) ? 'nav-link--active' : ''}`}
              onClick={() => setConciergeDropdownOpen(!conciergeDropdownOpen)}
              aria-expanded={conciergeDropdownOpen}
              aria-haspopup="true"
            >
              Concierge
              <svg className={`nav-dropdown-chevron ${conciergeDropdownOpen ? 'nav-dropdown-chevron--open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {conciergeDropdownOpen && (
              <div className="nav-dropdown" role="menu">
                <Link to="/concierge" className={`nav-dropdown__item ${isActive('/concierge') ? 'nav-dropdown__item--active' : ''}`} role="menuitem" onClick={() => setConciergeDropdownOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                  Concierge Board
                </Link>
                <Link to="/lists" className={`nav-dropdown__item ${isActive('/lists') ? 'nav-dropdown__item--active' : ''}`} role="menuitem" onClick={() => setConciergeDropdownOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                  Lists
                </Link>
              </div>
            )}
          </div>
          <div className="nav-dropdown-wrap" ref={campsDropdownRef}>
            <button
              className={`nav-link ${isActiveGroup(['/camps', '/camp-data', '/webinars']) ? 'nav-link--active' : ''}`}
              onClick={() => setCampsDropdownOpen(!campsDropdownOpen)}
              aria-expanded={campsDropdownOpen}
              aria-haspopup="true"
            >
              Camps & Webinars
              <svg className={`nav-dropdown-chevron ${campsDropdownOpen ? 'nav-dropdown-chevron--open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {campsDropdownOpen && (
              <div className="nav-dropdown" role="menu">
                <Link to="/camps" className={`nav-dropdown__item ${isActive('/camps') ? 'nav-dropdown__item--active' : ''}`} role="menuitem" onClick={() => setCampsDropdownOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                  Camps & Clinics
                </Link>
                {(user?.role === 'admin' || user?.role === 'internal_staff') && (
                  <Link to="/camps/checkin" className={`nav-dropdown__item ${isActive('/camps/checkin') ? 'nav-dropdown__item--active' : ''}`} role="menuitem" onClick={() => setCampsDropdownOpen(false)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                    Camp Check-In
                  </Link>
                )}
                {user?.role === 'admin' && (
                  <Link to="/camp-data" className={`nav-dropdown__item ${isActive('/camp-data') ? 'nav-dropdown__item--active' : ''}`} role="menuitem" onClick={() => setCampsDropdownOpen(false)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    Camp Data
                  </Link>
                )}
                <Link to="/admin/wristbands" className={`nav-dropdown__item ${isActive('/admin/wristbands') ? 'nav-dropdown__item--active' : ''}`} role="menuitem" onClick={() => setCampsDropdownOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41L13.42 20.58a2 2 0 0 1-2.83 0L2.59 12.58V2h10.58l7.42 7.41a2 2 0 0 1 0 2.83z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                  Wristbands
                </Link>
                <Link to="/webinars" className={`nav-dropdown__item ${isActive('/webinars') ? 'nav-dropdown__item--active' : ''}`} role="menuitem" onClick={() => setCampsDropdownOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
                  Webinars
                </Link>
              </div>
            )}
          </div>
          <Link to="/players" className={`nav-link ${isActive('/players') ? 'nav-link--active' : ''}`}>Search</Link>
          <div className="nav-dropdown-wrap" ref={adminDropdownRef}>
            <button
              className={`nav-link ${isActiveGroup(['/admin/']) ? 'nav-link--active' : ''}`}
              onClick={() => setAdminDropdownOpen(!adminDropdownOpen)}
              aria-expanded={adminDropdownOpen}
              aria-haspopup="true"
            >
              Admin
              <svg className={`nav-dropdown-chevron ${adminDropdownOpen ? 'nav-dropdown-chevron--open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {adminDropdownOpen && (
              <div className="nav-dropdown" role="menu">
                <Link to="/admin/users" className={`nav-dropdown__item ${isActive('/admin/users') ? 'nav-dropdown__item--active' : ''}`} role="menuitem" onClick={() => setAdminDropdownOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  Manage Users
                </Link>
                <Link to="/admin/content" className={`nav-dropdown__item ${isActive('/admin/content') ? 'nav-dropdown__item--active' : ''}`} role="menuitem" onClick={() => setAdminDropdownOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  Content
                </Link>
                <Link to="/admin/reports" className={`nav-dropdown__item ${isActive('/admin/reports') ? 'nav-dropdown__item--active' : ''}`} role="menuitem" onClick={() => setAdminDropdownOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18"/><path d="M7 14l4-4 4 4 5-6"/></svg>
                  Reports
                </Link>
              </div>
            )}
          </div>
          <div className="nav-dropdown-wrap" ref={messagesDropdownRef}>
            <button
              className={`nav-link ${isActiveGroup(['/messages', '/admin/flags', '/admin/drip-campaigns']) ? 'nav-link--active' : ''}`}
              onClick={() => setMessagesDropdownOpen(!messagesDropdownOpen)}
              aria-expanded={messagesDropdownOpen}
              aria-haspopup="true"
            >
              Messages
              {unreadMessages > 0 && <span className="nav-badge">{unreadMessages}</span>}
              <svg className={`nav-dropdown-chevron ${messagesDropdownOpen ? 'nav-dropdown-chevron--open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {messagesDropdownOpen && (
              <div className="nav-dropdown" role="menu">
                <Link to="/messages" className={`nav-dropdown__item ${isActive('/messages') ? 'nav-dropdown__item--active' : ''}`} role="menuitem" onClick={() => setMessagesDropdownOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  Messages
                  {unreadMessages > 0 && <span className="user-dropdown__badge">{unreadMessages}</span>}
                </Link>
                <Link to="/admin/flags" className={`nav-dropdown__item ${isActive('/admin/flags') ? 'nav-dropdown__item--active' : ''}`} role="menuitem" onClick={() => setMessagesDropdownOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
                  Flagged Messages
                  {pendingFlags > 0 && <span className="user-dropdown__badge">{pendingFlags}</span>}
                </Link>
                <Link to="/admin/drip-campaigns" className={`nav-dropdown__item ${isActive('/admin/drip-campaigns') ? 'nav-dropdown__item--active' : ''}`} role="menuitem" onClick={() => setMessagesDropdownOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  Drip Campaigns
                </Link>
              </div>
            )}
          </div>
        </>
      );
    }

    // Fallback
    return <Link to="/dashboard" className={`nav-link ${isActive('/dashboard') ? 'nav-link--active' : ''}`}>Dashboard</Link>;
  };

  return (
    <div className="layout layout--ed layout--darknav">
      <a href="#main-content" className="skip-to-content">Skip to main content</a>
      <nav className={`navbar ${usesRail ? 'navbar--search' : ''}`} aria-label="Main navigation">
        <div className="navbar-left">
          <button className="navbar-brand" onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} aria-label="ProZpects home">
            <img src="/logo-wordmark.svg" alt="ProZpects" className="navbar-logo" />
          </button>

          {/* Admins and partners navigate from the left rail (components/AdminShell.js), so
              the top bar carried a second, overlapping copy of the same
              destinations — Search, Camps and the whole ADMIN dropdown were in
              both. The bar gives that space to the search instead.
              EVERY OTHER ROLE KEEPS ITS LINKS: an athlete, coach, guardian,
              or marketing user has no left rail, so removing these for
              them would leave no navigation at all. */}
          {!usesRail && (
            <button
              className="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-label="Toggle navigation menu"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {mobileMenuOpen ? (
                  <path d="M18 6L6 18M6 6l12 12" />
                ) : (
                  <path d="M3 12h18M3 6h18M3 18h18" />
                )}
              </svg>
            </button>
          )}

          {usesRail ? (
            <div className="navbar-search">
              <AdminSearchBar />
            </div>
          ) : (
            <div className={`navbar-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
              {renderNavLinks()}
            </div>
          )}
        </div>

        <div className="navbar-right">
          {usesRail && (
            <span className="navbar-context">{isAdmin ? 'ProZpects · Admin' : 'ProZpects · Partner'}</span>
          )}
          {/* Notification bell */}
          <button
            className={`nav-icon-btn ${isActive(isAdmin ? '/admin/flags' : '/messages') ? 'nav-icon-btn--active' : ''}`}
            onClick={() => navigate(isAdmin ? '/admin/flags' : '/messages')}
            title={isAdmin ? 'Notifications' : 'Messages'}
            aria-label={isAdmin ? 'Notifications' : 'Messages'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            {notificationCount > 0 && (
              <span className="nav-icon-badge">{notificationCount}</span>
            )}
          </button>

          {/* User menu */}
          <div className="user-menu-wrap" ref={dropdownRef}>
            <button
              className="user-menu-trigger"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              aria-expanded={dropdownOpen}
              aria-haspopup="true"
              aria-label="User menu"
            >
              <span className="user-avatar">{getInitials()}</span>
              <span className="user-menu-name">{user?.first_name || user?.email}</span>
              <svg className={`user-menu-chevron ${dropdownOpen ? 'user-menu-chevron--open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {dropdownOpen && (
              <div className="user-dropdown" role="menu">
                <div className="user-dropdown__header">
                  <div className="user-dropdown__name">{user?.first_name} {user?.last_name}</div>
                  <div className="user-dropdown__email">{user?.email}</div>
                </div>
                <div className="user-dropdown__divider" />

                {/* Share Profile (athlete with profile URL) */}
                {isPlayer && profileUrl && (
                  <button className="user-dropdown__item" role="menuitem" onClick={() => { setDropdownOpen(false); handleShare(); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                    Share Profile
                  </button>
                )}

                {/* Profile */}
                {profilePath && (
                  <button className={`user-dropdown__item ${isActiveGroup(profileActivePaths) ? 'user-dropdown__item--active' : ''}`} role="menuitem" onClick={() => { setDropdownOpen(false); navigate(profilePath); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    Profile
                  </button>
                )}

                {/* Settings */}
                <button className="user-dropdown__item" role="menuitem" onClick={() => { setDropdownOpen(false); navigate('/settings'); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                  </svg>
                  Settings
                </button>

                <div className="user-dropdown__divider" />

                {/* Log out */}
                <button className="user-dropdown__item user-dropdown__item--danger" role="menuitem" onClick={handleLogout}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      <main className={`main-content ${usesRail ? 'main-content--rail' : ''}`} id="main-content">
        {user && user.is_approved === false && (
          <div className="approval-banner" role="alert">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            Your account is pending admin approval. Some features may be limited until your account is confirmed.
          </div>
        )}
        <EmailVerificationBanner />
        {children}
      </main>
    </div>
  );
}
